import LinearProgress from "@material-ui/core/LinearProgress";
import ReactMarkdown from "react-markdown";

import HiddenElement from "components/molecules/HiddenElement";
import classes from "components/molecules/ViewDetailsModal/style.module.css";
import { IPLResultWarning } from "components/molecules/ViewDetailsModal/warnings/IPLResultWarning";
import { useGetIPLResultDetailsAISummary } from "reactQuery/hooks/useIPLResults";

import { InsightSummaryTemplate } from "./InsightSummaryTemplate";

export const IPLResultTemplate = ({ result }: { result: any }) => {
    const iplSectionEnabled = !!result.id;
    const aiSummaryQuery = useGetIPLResultDetailsAISummary(
        result.id,
        iplSectionEnabled
    );

    const summarysnippet = (
        <>
            <hr className={classes.line} />
            <p className={classes["snippet-title"]}>Snippet</p>
            {aiSummaryQuery.isLoading ? (
                <>
                    <LinearProgress />
                    <p>Loading Snippet...</p>
                </>
            ) : (
                <p
                    dangerouslySetInnerHTML={{
                        __html: aiSummaryQuery.data?.ai_summary,
                    }}
                />
            )}
        </>
    );
    const fullSnippet = (
        <>
            {summarysnippet}
            <hr className={classes.line} />
            <p className={classes["snippet-title"]}>Original Snippet</p>
            <p>{result.show_longer_snippet}</p>
        </>
    );

    const isLLMIndicatorProject = result.ipl_label?.name?.includes("Indicator Project");
    const isNotLLMProject = result.ipl_label?.name?.includes("Not A Project");

    const generateLLMPredictedReasoningSnippet = () => {
        const llmReasoningCategories = {
            Reasoning: result.ipl_label.reasoning
                .match(/Reasoning: (.*?)(?=\nProject Evidence:|$)/s)?.[1]
                .trim(),
            "Project Evidence": result.ipl_label.reasoning
                .match(/Project Evidence: (.*?)(?=\nFunding:|$)/s)?.[1]
                .trim(),
            Funding: result.ipl_label.reasoning
                .match(/Funding: (.*?)(?=\n|$)/s)?.[1]
                .trim(),
        };

        const llmReasoningStr = llmReasoningCategories
            ? Object.entries(llmReasoningCategories)
                  .map(([key, value]) => (value ? `**${key}:** ${value}` : null))
                  .filter(Boolean)
                  .join("\n\n")
            : "";

        return (
            <>
                <hr className={classes.line} />
                <div className={classes["llm-snippet-section"]}>
                    <p className={classes["snippet-title"]}>LLM Predicted Reasoning</p>
                    <div>
                        <ReactMarkdown>{llmReasoningStr}</ReactMarkdown>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div style={{ fontSize: "17px" }}>
            {iplSectionEnabled ? (
                <>
                    <p>
                        <a
                            target="_blank"
                            className={classes["no-underline"]}
                            href={`/admin/target_reports/intelligencepipelineresult/${result.id}/change/`}
                        >
                            {result.id}
                        </a>
                    </p>
                    {result.warning_labels ? (
                        <p>
                            {result.warning_labels.map((warning) => (
                                <IPLResultWarning warning={warning} />
                            ))}
                        </p>
                    ) : null}
                    <p style={{ margin: "4px 0" }}>
                        <strong>{result.owner_text}</strong>
                    </p>
                    <p style={{ margin: "4px 0" }}>{result.industry_text}</p>
                    <p style={{ margin: "4px 0" }}>{result.indicator_text}</p>
                    <p>
                        <strong>{result.query_text}</strong>
                    </p>
                    {result.indicator ? (
                        <>
                            {result.indicator.description ? (
                                <p>
                                    <HiddenElement
                                        fullElement={
                                            <>
                                                <p>Tutorial</p>
                                                <p>{result.indicator.description}</p>
                                                {result.indicator.tutorial_link ? (
                                                    <p>
                                                        {result.indicator.tutorial_link}
                                                    </p>
                                                ) : null}
                                            </>
                                        }
                                        expandText="Tutorial"
                                        retractText="Hide Tutorial"
                                    />
                                </p>
                            ) : null}
                        </>
                    ) : null}
                    <p>
                        <HiddenElement
                            fullElement={fullSnippet}
                            hiddenElement={summarysnippet}
                            expandText="Show Original"
                            retractText="Hide"
                        />
                    </p>
                    {result.ipl_label?.reasoning ? (
                        <p>
                            <HiddenElement
                                fullElement={generateLLMPredictedReasoningSnippet()}
                                expandText="LLM Predicted Reasoning"
                                retractText="Hide"
                                defaultVisibility={true}
                            />
                        </p>
                    ) : null}
                    <hr className={classes.line} />
                    <p>
                        {result.url ? (
                            <a
                                className={classes["no-underline"]}
                                href={result.url}
                                target="_blank"
                            >
                                {result.title}
                            </a>
                        ) : null}
                        {result.url && result.public_url ? <>&nbsp;</> : null}
                        {result.public_url ? (
                            <a
                                className={classes["no-underline"]}
                                href={result.public_url}
                                target="_blank"
                            >
                                (Converted Text File)
                            </a>
                        ) : null}
                        <p>
                            Created on{" "}
                            <strong>
                                {new Date(result.date_created).toLocaleDateString(
                                    "en-US",
                                    {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    }
                                )}
                            </strong>
                        </p>
                    </p>
                </>
            ) : null}
            {result.ipl_label?.name ? (
                <div className={classes["llm-predicted-container"]}>
                    <p>LLM Predicted:</p>&nbsp;
                    <p
                        className={classes["llm-predicted-label"]}
                        style={
                            isLLMIndicatorProject
                                ? { color: "green" }
                                : isNotLLMProject
                                ? { color: "red" }
                                : {}
                        }
                    >
                        {result.ipl_label.name}
                    </p>
                </div>
            ) : null}
            {result.insight ? <InsightSummaryTemplate iplResult={result} /> : null}
        </div>
    );
};
